/**
 * A rotina noturna do Recurso pelo SGR (spec 035, D2–D7 e D10).
 *
 * Toda noite, numa janela de São Paulo, o ERP passa pelos tickets ABERTOS que
 * trazem o `ID_SGR:` do worker na descrição:
 * - o que ainda não tem Recurso é LIGADO ao veículo, como a escolha de uma
 *   pessoa: o veículo e o associado são gravados (o SGR vence ali), e a ficha
 *   recebe só os campos VAZIOS (D2);
 * - o que já tem Recurso só tem o veículo e o associado relidos. O ticket não
 *   muda: a diferença vira aviso no cartão, e uma pessoa decide (D5).
 *
 * Aqui mora o que a API e o navegador precisam concordar: o que a rotina
 * preenche (a MESMA régua da escolha de uma pessoa, `existingTicketPickPlan`),
 * a comparação do aviso "o SGR mudou" e o contrato do status da rotina.
 */
import { z } from 'zod';
import { Dateish } from './common';
import {
  TICKET_SHEET_SLOTS,
  type ResolvedSheetSlot,
  type TicketSheetSlot,
  type TicketSheetSlots,
} from './ticket-sheet';

// ── a escolha de uma pessoa num ticket existente (spec 032, §4) ─────────────

export interface ExistingTicketPickPlan {
  /** os slots VAZIOS na ficha efetiva, que a escolha preenche */
  fill: Partial<TicketSheetSlots>;
  /** os que já têm valor diferente: "No SGR: X", com "usar" — nada sobrescrito sem clique */
  differs: Array<{ slot: TicketSheetSlot; current: string; sgrValue: string }>;
}

/**
 * O campo Recurso de um ticket existente (spec 032, §4): o slot VAZIO na ficha
 * efetiva é preenchido; o que tem valor fica, e a tela mostra o do SGR ao lado.
 * Mora aqui (e não só no web) porque a rotina noturna usa a MESMA régua (D2).
 */
export function existingTicketPickPlan(
  effective: Record<TicketSheetSlot, string | null>,
  sgr: TicketSheetSlots
): ExistingTicketPickPlan {
  const plan: ExistingTicketPickPlan = { fill: {}, differs: [] };
  for (const slot of TICKET_SHEET_SLOTS) {
    const value = sgr[slot];
    if (value === null || value === '') continue;
    const cur = effective[slot];
    if (cur === null || cur.trim() === '') plan.fill[slot] = value;
    else if (cur !== value) plan.differs.push({ slot, current: cur, sgrValue: value });
  }
  return plan;
}

// ── o que a rotina preenche (D2) ────────────────────────────────────────────

export interface SgrRoutineFillPlan extends ExistingTicketPickPlan {
  /** slots que uma PESSOA apagou: vazios na ficha, e a rotina não os preenche */
  erased: TicketSheetSlot[];
}

/**
 * O que a rotina grava na ficha na PRIMEIRA ligação: a régua da escolha de uma
 * pessoa (`existingTicketPickPlan` sobre o valor EFETIVO, de qualquer camada),
 * com UMA diferença, de propósito: o slot que uma pessoa APAGOU (`''` na camada
 * dela) é uma decisão dela — o telefone apagado saiu do disparo, da busca e do
 * bot —, e quem não é pessoa não o desfaz. A pessoa, ao escolher o veículo,
 * decide de novo; a rotina não decide por ela.
 *
 * Nunca sobrescreve valor efetivo não vazio: o da descrição do worker, o do
 * título e o que a pessoa escreveu ficam, e a diferença aparece como "No SGR".
 */
export function sgrRoutineFillPlan(
  resolved: Record<TicketSheetSlot, Pick<ResolvedSheetSlot, 'value' | 'source'>>,
  sgr: TicketSheetSlots
): SgrRoutineFillPlan {
  const effective = {} as Record<TicketSheetSlot, string | null>;
  for (const slot of TICKET_SHEET_SLOTS) effective[slot] = resolved[slot].value;
  const plan = existingTicketPickPlan(effective, sgr);
  const erased: TicketSheetSlot[] = [];
  for (const slot of TICKET_SHEET_SLOTS) {
    if (plan.fill[slot] === undefined || resolved[slot].source !== 'person') continue;
    delete plan.fill[slot];
    erased.push(slot);
  }
  return { ...plan, erased };
}

// ── o aviso "o SGR mudou" (D5) ──────────────────────────────────────────────

/**
 * Os slots em que o registro ATUAL do veículo e do associado (a última leitura
 * do SGR, `equipments.sgr_sheet`) difere da FOTO do ticket (`sheet_from_sgr`).
 * É o "O SGR mudou N campos desde DD/MM" do cartão. Comparação exata, slot a
 * slot: as duas saem da mesma função do servidor (sheetFromSgrDetail).
 */
export function sgrSheetDrift(photo: TicketSheetSlots, current: TicketSheetSlots): TicketSheetSlot[] {
  const norm = (v: string | null | undefined) => (v === undefined || v === '' ? null : v);
  return TICKET_SHEET_SLOTS.filter((slot) => norm(photo[slot]) !== norm(current[slot]));
}

/** Como o detalhe do ticket o devolve; null = não há com o que comparar. */
export const SgrSheetDriftSchema = z.object({
  /** os slots que mudaram no SGR desde a foto (vazio = nada mudou) */
  slots: z.array(z.string()),
  /** a última leitura do veículo (quando o registro atual foi lido) */
  readAt: Dateish.nullable(),
});
export type SgrSheetDrift = z.infer<typeof SgrSheetDriftSchema>;

// ── a rodada (D7) ───────────────────────────────────────────────────────────

/** A marca da foto e do histórico escritos pela rotina — não há usuário (D4). */
export const SGR_ROUTINE_PHOTO_MARK = 'daily' as const;
/** `ticket_events.payload.source` do que a rotina escreveu */
export const SGR_ROUTINE_EVENT_SOURCE = 'sgr_routine' as const;

export const SGR_ROUTINE_TRIGGERS = ['schedule', 'manual'] as const;
export type SgrRoutineTrigger = (typeof SGR_ROUTINE_TRIGGERS)[number];

export const SGR_ROUTINE_RUN_STATUSES = ['running', 'ok', 'error'] as const;
export type SgrRoutineRunStatus = (typeof SGR_ROUTINE_RUN_STATUSES)[number];

/**
 * Por que a rodada parou. `done`, `window` e `budget` são o fim normal (`ok`): a
 * noite seguinte continua do cursor. Os outros são `error`.
 */
export const SGR_ROUTINE_STOP_REASONS = [
  /** passou por todos os veículos da fila */
  'done',
  /** a janela da noite acabou (ou, na manual, o tempo da janela) */
  'window',
  /** o orçamento de leituras da noite acabou */
  'budget',
  /** o SGR não respondeu (login recusado, ou falhas seguidas) */
  'sgr_unavailable',
  /** a rodada anterior não terminou (processo reiniciado) */
  'stale',
  /** erro inesperado */
  'crashed',
] as const;
export type SgrRoutineStopReason = (typeof SGR_ROUTINE_STOP_REASONS)[number];

export const SGR_ROUTINE_STOP_LABELS: Record<SgrRoutineStopReason, string> = {
  done: 'Passou por todos os tickets da fila.',
  window: 'A janela da noite acabou; a próxima continua de onde parou.',
  budget: 'O limite de leituras da noite acabou; a próxima continua de onde parou.',
  sgr_unavailable: 'A origem não respondeu; a próxima noite tenta de novo.',
  stale: 'A rodada não terminou (processo reiniciado).',
  crashed: 'A rodada parou por um erro inesperado.',
};

/**
 * A SOMBRA da regra de abertura (spec 046, fase 1): para cada ticket do SGR que
 * o espelho trouxe nas últimas 24 h, o que o ERP decidiria (a mesa pela
 * situação do veículo, o cliente pelo ponto de venda, a ficha pelas regras do
 * worker) contra o que o worker fez. Só contagens: a ficha se compara por hash,
 * e nenhum valor sai daqui. A sombra não escreve nada além destas contagens.
 */
export const SgrRoutineShadowCountsSchema = z.object({
  /** tickets do worker que chegaram pelo espelho na janela (abertos, com `ID_SGR:`) */
  tickets: z.number().int(),
  /** desses, os que tiveram o veículo lido nesta rodada e foram comparados */
  compared: z.number().int(),
  /** os que ficaram sem leitura (orçamento, janela, SGR fora, veículo sumido) — deve ser 0 */
  notRead: z.number().int(),
  /** a mesa prevista pela situação (85316 para `INSTALAR CNPJ/CPF`, 35886 para `INSTALAR*`) é a do ticket */
  deskEqual: z.number().int(),
  deskDifferent: z.number().int(),
  /** a situação do veículo já saiu de `INSTALAR*`: não há mesa a prever */
  deskNoDecision: z.number().int(),
  /** o cliente do ponto de venda (o ligado; sem ligação, o sugerido) é o do ticket */
  clientEqual: z.number().int(),
  clientDifferent: z.number().int(),
  /** veículo sem ponto de venda, ou ponto sem cliente ligado nem sugerido */
  clientNoSalePoint: z.number().int(),
  /** telefones e endereço da descrição do worker iguais aos da ficha do ERP (por hash) */
  sheetEqual: z.number().int(),
  sheetDifferent: z.number().int(),
  /** a situação saiu de `INSTALAR*` desde a abertura: a ficha não é comparada */
  sheetSituationChanged: z.number().int(),
  /** dos diferentes: os telefones (o par, na ordem) */
  phonesDifferent: z.number().int(),
  /** dos diferentes: o endereço (logradouro, número, complemento, bairro, cidade, UF) */
  addressDifferent: z.number().int(),
});
export type SgrRoutineShadowCounts = z.infer<typeof SgrRoutineShadowCountsSchema>;

export const EMPTY_SGR_ROUTINE_SHADOW_COUNTS: Readonly<SgrRoutineShadowCounts> = Object.freeze({
  tickets: 0,
  compared: 0,
  notRead: 0,
  deskEqual: 0,
  deskDifferent: 0,
  deskNoDecision: 0,
  clientEqual: 0,
  clientDifferent: 0,
  clientNoSalePoint: 0,
  sheetEqual: 0,
  sheetDifferent: 0,
  sheetSituationChanged: 0,
  phonesDifferent: 0,
  addressDifferent: 0,
});

/** Contagens — o que vai ao log e à tela. Nunca placa, nome, telefone ou id. */
export const SgrRoutineCountsSchema = z.object({
  /** tickets abertos com o código do veículo (a fila da rodada) */
  selected: z.number().int(),
  /** veículos distintos na fila */
  vehicles: z.number().int(),
  /** veículos lidos com sucesso nesta rodada */
  read: z.number().int(),
  /** tickets ligados ao Recurso pela primeira vez */
  linked: z.number().int(),
  /** campos vazios da ficha preenchidos nesses tickets */
  filledSlots: z.number().int(),
  /** tickets NÃO ligados: o veículo é de outra associação no SGR (D6) */
  otherClient: z.number().int(),
  /** tickets já ligados cujo veículo foi relido (o ticket não muda) */
  refreshed: z.number().int(),
  /** veículos que o SGR não tem mais */
  notFound: z.number().int(),
  /** leituras de veículo que falharam (SGR indisponível) */
  unavailable: z.number().int(),
  /** tickets que mudaram no meio (fechados, ligados por uma pessoa) e ficaram como estavam */
  skipped: z.number().int(),
  /** veículos ou tickets em que a GRAVAÇÃO no ERP falhou (o motivo vai ao log, só o código) */
  errors: z.number().int(),
  /** chamadas ao SGR feitas pela rodada (o orçamento) */
  reads: z.number().int(),
  /** logins no SGR durante a rodada */
  logins: z.number().int(),
  /** desses, quantos refizeram uma sessão que caiu */
  relogins: z.number().int(),
  /** a sombra da regra de abertura (spec 046, fase 1); ausente nas rodadas de antes dela */
  shadow: SgrRoutineShadowCountsSchema.optional(),
});
export type SgrRoutineCounts = z.infer<typeof SgrRoutineCountsSchema>;

export const EMPTY_SGR_ROUTINE_COUNTS: Readonly<SgrRoutineCounts> = Object.freeze({
  selected: 0,
  vehicles: 0,
  read: 0,
  linked: 0,
  filledSlots: 0,
  otherClient: 0,
  refreshed: 0,
  notFound: 0,
  unavailable: 0,
  skipped: 0,
  errors: 0,
  reads: 0,
  logins: 0,
  relogins: 0,
});

export const SgrRoutineRunSchema = z.object({
  id: z.number().int(),
  trigger: z.enum(SGR_ROUTINE_TRIGGERS),
  triggeredBy: z.object({ id: z.number().int(), name: z.string() }).nullable(),
  status: z.enum(SGR_ROUTINE_RUN_STATUSES),
  /** null enquanto roda */
  stoppedBy: z.enum(SGR_ROUTINE_STOP_REASONS).nullable(),
  /** parciais enquanto roda; finais quando termina */
  counts: SgrRoutineCountsSchema.nullable(),
  startedAt: Dateish,
  finishedAt: Dateish.nullable(),
});
export type SgrRoutineRun = z.infer<typeof SgrRoutineRunSchema>;

/**
 * GET /sgr/routine (admin). `available: false` = este tenant não é o da
 * integração. Com `available`, `enabled: false` diz o que falta — só NOMES.
 */
export const SgrRoutineStatusSchema = z.union([
  z.object({ available: z.literal(false) }),
  z.object({
    available: z.literal(true),
    enabled: z.boolean(),
    missingConfig: z.array(z.string()),
    sourceName: z.string(),
    /** "toda noite, das 03:00 às 06:00 (horário de Brasília)" */
    scheduleLabel: z.string(),
    readsPerNight: z.number().int(),
    lastRun: SgrRoutineRunSchema.nullable(),
  }),
]);
export type SgrRoutineStatus = z.infer<typeof SgrRoutineStatusSchema>;
